import React, { useContext } from 'react';
import { css, useTheme } from '@emotion/react';
import { migrateCard } from '@bbkAdminComponents/experiences/migrations/offers';
import { EditOfferPageContext } from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import {
  isOfferCardPack,
  type RestMultiRowSurveyCard,
  type SurveyType,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import {
  RestModalTemplates,
  type HeroImageV1,
  type RestModalCard,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { OfferPresentation } from '@bbkAdminUtils/api-client/rest-offers-api';
import { cs } from '@bbkAdminUtils/css-utils';
import { useDetectIE } from '@bbkAdminUtils/detect-ie';
import { type ReactFCC } from '@bbkAdminUtils/utility-types';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { type ModalActionHandler } from '@bbkFunnelComponents/modal';
import NoteModal1 from '@bbkFunnelComponents/modals/4060-mini-modal-1';
import NoteModal2 from '@bbkFunnelComponents/modals/4060-mini-modal-2';
import NoteModal3 from '@bbkFunnelComponents/modals/4060-mini-modal-3';
import NoteModal4 from '@bbkFunnelComponents/modals/4060-mini-modal-4';
import FiftyFiftyColumn from '@bbkFunnelComponents/modals/5050-column-layout';
import { SixtyFortyColumn } from '@bbkFunnelComponents/modals/6040-column-layout';
import SeventyThirtyRow from '@bbkFunnelComponents/modals/7030-row-layout';
import { ConfirmCancellationModal } from '@bbkFunnelComponents/modals/confirm-cancelation-modal';
import { EmailModal } from '@bbkFunnelComponents/modals/email-modal';
import { EmailModalSuccess } from '@bbkFunnelComponents/modals/email-modal-success';
import IntercomModalPlaceholder from '@bbkFunnelComponents/modals/intercom-modal-placeholder';
import { ModalDetailedActions } from '@bbkFunnelComponents/modals/sections/detailed-actions';
import ModalG2Quotes from '@bbkFunnelComponents/modals/sections/g2_quotes';
import { ModalImage } from '@bbkFunnelComponents/modals/sections/image';
import { ModalActions } from '@bbkFunnelComponents/modals/sections/modal-actions';
import ModalMultiQuoteTop from '@bbkFunnelComponents/modals/sections/multi-quote-top';
import ModalOfferCode from '@bbkFunnelComponents/modals/sections/offer-code';
import ModalQuote from '@bbkFunnelComponents/modals/sections/quote';
import ModalSimpleActions from '@bbkFunnelComponents/modals/sections/simple-actions';
import ModalTitle from '@bbkFunnelComponents/modals/sections/title';
import SpeechBubble1 from '@bbkFunnelComponents/modals/speech-bubble-1';
import SpeechBubble2 from '@bbkFunnelComponents/modals/speech-bubble-2';
import VideoRowModal from '@bbkFunnelComponents/modals/video-row';
import CustomIcons from '@bbkFunnelUtils/icons';
import {
  FortySixtyMiniModal1V2,
  FortySixtyMiniModal2V2,
  LCOBanner,
  LCOModal,
  LCOPopup,
  OfferCardPack,
  SixtyFortyColumnV2,
  SpeechBubbleV2,
  VideoV2,
} from './modals';
import {
  ConfirmCancel,
  EmailCompose,
  FinalOffer,
  MessageSuccess,
  MultiCTATypeI,
  MultiCTATypeII,
  RefundCancelModal,
  StructuredContent,
} from './modals/v2';

import '@bbkFunnelStylesheets/index.scss';

type WrapperProps = {
  modal: RestModalCard;
  children: React.FunctionComponentElement<any>;
  isPreview?: boolean;
  nevermind: () => void;
};

const Wrapper10: ReactFCC<WrapperProps> = (props) => {
  const { modal, isPreview, nevermind, children } = props;
  return (
    <div className={cs('modal-content', modal.template)}>
      <div className={cs('modal-body', modal.name)}>
        <div className="bb-modal">
          <div className="close-button-wrap">
            {modal.dismissable !== false && (
              <button
                onClick={() => {
                  if (isPreview) return;
                  nevermind();
                }}
                style={{
                  color: children.type.closeBtnFill || '#9d9d9d',
                }}
                className="modal-close-btn"
                id="modal-close-btn"
                type="button"
              >
                {CustomIcons.closeModal}
              </button>
            )}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

type Props = {
  card: RestModalCard;
  isPreview?: boolean;
  nevermind?: () => void;
  response?: RestMultiRowSurveyCard['provides'];
  questions?: RestMultiRowSurveyCard['requires']['multi_row_survey'];
  surveyType?: SurveyType;
  handleAction?: ModalActionHandler;
  handleBackwards?: ModalActionHandler;
  showCode?: boolean;
  offerManagementPage?: boolean;
};
const ModalsFactory: React.FC<Props> = (props) => {
  const {
    card: propsModal,
    isPreview,
    nevermind = () => undefined,
    response = {},
    questions = [],
    surveyType = '',
    handleAction = () => undefined,
    handleBackwards = () => undefined,
    showCode,
    offerManagementPage,
  } = props;

  const theme = useTheme() as EmotionTheme;
  const isIE = useDetectIE();

  const modal = migrateCard(propsModal);

  const offerContext = useContext(EditOfferPageContext);

  const isBanner =
    offerContext?.offer.presentation === OfferPresentation.banner;

  const getModalContent = (): React.ReactNode => {
    let modalContent = <div>Before matching</div>;

    if (!isIE) {
      switch (modal.template) {
        case RestModalTemplates.sixty_forty_column:
          return (
            <SixtyFortyColumnV2
              modalInfo={modal}
              close={nevermind}
              detailedAction={
                <ModalSimpleActions
                  onClick={handleAction}
                  fullModalInfo={modal}
                  hide={nevermind}
                  version2Template
                />
              }
            />
          );
        case RestModalTemplates.offer_card_pack:
          return (
            <OfferCardPack
              isPreview={isPreview}
              modalInfo={modal}
              close={nevermind}
              handleAction={handleAction}
            />
          );
        case RestModalTemplates.lco_banner:
          return (
            <LCOBanner
              isPreview={Boolean(isPreview)}
              modalInfo={modal}
              close={nevermind}
              detailedAction={
                <ModalSimpleActions
                  onClick={handleAction}
                  fullModalInfo={modal}
                  hide={nevermind}
                  version2Template
                />
              }
            />
          );
        case RestModalTemplates.lco_modal:
          return (
            <LCOModal
              modalInfo={modal}
              close={nevermind}
              detailedAction={
                <ModalSimpleActions
                  onClick={handleAction}
                  fullModalInfo={modal}
                  hide={nevermind}
                  version2Template
                />
              }
            />
          );
        case RestModalTemplates.lco_popup:
          return (
            <LCOPopup
              modalInfo={modal}
              close={nevermind}
              detailedAction={
                <ModalSimpleActions
                  onClick={handleAction}
                  fullModalInfo={modal}
                  hide={nevermind}
                  version2Template
                />
              }
            />
          );
        case RestModalTemplates.forty_sixty_mini_modal_1:
          return (
            <FortySixtyMiniModal1V2
              modalInfo={modal}
              close={nevermind}
              detailedAction={
                <ModalSimpleActions
                  onClick={handleAction}
                  fullModalInfo={modal}
                  hide={nevermind}
                  version2Template
                />
              }
            />
          );
        // left side with gray bg and right side with content
        case RestModalTemplates.forty_sixty_mini_modal_2:
          return (
            <FortySixtyMiniModal2V2
              modalInfo={modal}
              close={nevermind}
              detailedAction={
                <ModalSimpleActions
                  onClick={handleAction}
                  fullModalInfo={modal}
                  hide={nevermind}
                  version2Template
                />
              }
            />
          );
        // left side with gray bg and right side with content
        case RestModalTemplates.speech_bubble_2:
          return (
            <SpeechBubbleV2
              modalInfo={modal}
              close={nevermind}
              detailedAction={
                <ModalSimpleActions
                  onClick={handleAction}
                  fullModalInfo={modal}
                  hide={nevermind}
                  version2Template
                />
              }
            />
          );
        case RestModalTemplates.video_row:
          return (
            <VideoV2
              modalInfo={modal}
              close={nevermind}
              detailedAction={
                <ModalSimpleActions
                  onClick={handleAction}
                  fullModalInfo={modal}
                  hide={nevermind}
                  version2Template
                />
              }
            />
          );
        case RestModalTemplates.modal_email_send:
          return (
            <EmailCompose
              modalInfo={modal}
              close={nevermind}
              handleAction={handleAction}
            />
          );
        case RestModalTemplates.modal_email_structured:
          return (
            <StructuredContent
              modalInfo={modal}
              close={nevermind}
              handleAction={handleAction}
            />
          );
        case RestModalTemplates.modal_email_success:
          return (
            <MessageSuccess
              modalInfo={modal}
              close={nevermind}
              handleAction={handleAction}
            />
          );
        case RestModalTemplates.final_offer:
          return <FinalOffer modalInfo={modal} handleAction={handleAction} />;
        case RestModalTemplates.multi_cta_type_I:
          return (
            <MultiCTATypeI
              modalInfo={modal}
              handleAction={handleAction}
              close={nevermind}
            />
          );
        case RestModalTemplates.multi_cta_type_II:
          return (
            <MultiCTATypeII
              modalInfo={modal}
              handleAction={handleAction}
              close={nevermind}
            />
          );
        case RestModalTemplates.modal_confirm_cancel:
          return (
            <ConfirmCancel
              modalInfo={modal}
              handleAction={handleAction}
              close={nevermind}
            />
          );
        case RestModalTemplates.modal_refund_cancel:
          return (
            <RefundCancelModal modalInfo={modal} handleAction={handleAction} />
          );
      }
    }

    // Version 1 modals
    switch (modal.template) {
      case RestModalTemplates.sixty_forty_column:
        modalContent = (
          <SixtyFortyColumn
            image={<ModalImage info={modal.requires.hero as HeroImageV1} />}
            person={modal.requires.person}
            title={<ModalTitle info={modal.requires.info} />}
            detailedAction={
              <ModalSimpleActions
                onClick={handleAction}
                fullModalInfo={modal}
                hide={nevermind}
              />
            }
            class={modal.name}
            fullModalInfo={modal}
          />
        );
        break;

      case RestModalTemplates.sixty_forty_column_action:
        modalContent = (
          <SixtyFortyColumn
            image={<ModalImage info={modal.requires.hero as HeroImageV1} />}
            title={<ModalTitle info={modal.requires.info} />}
            detailedAction={
              <ModalDetailedActions
                info={modal.requires.forwards}
                questions={modal.requires.questions}
                provides={modal.provides}
                name={modal.name}
                onClick={() => handleAction(modal.requires.forwards)}
              />
            }
            class={modal.name}
            fullModalInfo={modal}
          />
        );
        break;

      case RestModalTemplates.sixty_forty_column_code:
        modalContent = (
          <SixtyFortyColumn
            image={<ModalImage info={modal.requires.hero as HeroImageV1} />}
            offerCode={
              modal.requires.offerCode && (
                <ModalOfferCode offerCode={modal.requires.offerCode} />
              )
            }
            showCode={showCode}
            title={<ModalTitle info={modal.requires.info} headlineBig />}
            detailedAction={
              <ModalSimpleActions
                onClick={handleBackwards}
                fullModalInfo={modal}
                hide={nevermind}
              />
            }
            class={modal.name}
            fullModalInfo={modal}
          />
        );
        break;

      case RestModalTemplates.sixty_forty_column_g2:
        modalContent = (
          <SixtyFortyColumn
            quotes={<ModalG2Quotes hero={modal.requires.hero} />}
            title={<ModalTitle info={modal.requires.info} />}
            detailedAction={
              <ModalSimpleActions
                onClick={handleAction}
                fullModalInfo={modal}
                hide={nevermind}
              />
            }
            class={modal.name}
            fullModalInfo={modal}
          />
        );
        break;

      case RestModalTemplates.fifty_fifty_column:
        modalContent = (
          <FiftyFiftyColumn
            quote={<ModalQuote info={modal.requires.hero.quote} />}
            title={<ModalTitle info={modal.requires.info} />}
            detailedAction={
              <ModalSimpleActions
                onClick={handleAction}
                fullModalInfo={modal}
                hide={nevermind}
              />
            }
          />
        );
        break;

      case RestModalTemplates.seventy_thirty_row:
        modalContent = (
          <SeventyThirtyRow
            class={modal.name}
            title={<ModalTitle info={modal.requires.info} />}
            hero={<ModalMultiQuoteTop info={modal.requires.hero} />}
            actions={
              <ModalActions
                name={modal.name}
                mainAction={modal.requires.forwards}
                otherActions={modal.requires.other_actions ?? []}
                onClick={handleAction}
                intercomMessage="Things just aren't working."
              />
            }
          />
        );
        break;

      case RestModalTemplates.video_row:
        modalContent = (
          <VideoRowModal
            info={modal.requires.info}
            hero={modal.requires.hero}
            actions={
              <ModalSimpleActions
                onClick={handleAction}
                fullModalInfo={modal}
              />
            }
          />
        );
        break;

      case RestModalTemplates.speech_bubble_1:
        modalContent = (
          <SpeechBubble1
            modal={modal}
            info={modal.requires.info}
            person={modal.requires.person}
            actions={
              <ModalSimpleActions
                onClick={handleAction}
                fullModalInfo={modal}
                hide={nevermind}
              />
            }
          />
        );
        break;

      case RestModalTemplates.speech_bubble_2:
        modalContent = (
          <SpeechBubble2
            info={modal.requires.info}
            person={modal.requires.person}
            actions={
              <ModalSimpleActions
                onClick={handleAction}
                fullModalInfo={modal}
                hide={nevermind}
              />
            }
          />
        );
        // left side with white bg and right side with content
        break;

      case RestModalTemplates.forty_sixty_mini_modal_1:
        modalContent = (
          <NoteModal1
            info={modal.requires.info}
            person={modal.requires.person}
            hero={modal.requires.hero}
            actions={
              <ModalSimpleActions
                onClick={handleAction}
                fullModalInfo={modal}
                hide={nevermind}
              />
            }
          />
        );
        // left side with gray bg and right side with content
        break;

      case RestModalTemplates.forty_sixty_mini_modal_2:
        modalContent = (
          <NoteModal2
            info={modal.requires.info}
            person={modal.requires.person}
            hero={modal.requires.hero}
            actions={
              <ModalSimpleActions
                onClick={handleAction}
                fullModalInfo={modal}
                hide={nevermind}
              />
            }
          />
        );
        // left side with gray bg and right side with content
        break;

      case RestModalTemplates.forty_sixty_mini_modal_3:
        modalContent = (
          <NoteModal3
            info={modal.requires.info}
            person={modal.requires.person}
            forwards={modal.requires.forwards}
            backwards={modal.requires.backwards}
            response={response}
            handleAction={handleAction}
            questions={questions}
            surveyType={surveyType}
          />
        );
        // left side with gray bg and right side with content
        break;

      case RestModalTemplates.forty_sixty_mini_modal_4:
        modalContent = (
          <NoteModal4
            info={modal.requires.info}
            person={modal.requires.person}
            response={response}
            handleAction={() => handleAction(modal.requires.forwards, 'cancel')}
            questions={questions}
            surveyType={surveyType}
          />
        );
        break;

      case RestModalTemplates.intercom_chat_modal:
        modalContent = <IntercomModalPlaceholder modal={modal} />;
        break;

      case RestModalTemplates.modal_email_send:
        modalContent = <EmailModal onClick={handleAction} modal={modal} />;
        break;

      case RestModalTemplates.modal_email_success:
        modalContent = (
          <EmailModalSuccess
            onClick={handleAction}
            hide={nevermind}
            modal={modal}
          />
        );
        break;

      case RestModalTemplates.modal_confirm_cancel:
        modalContent = (
          <ConfirmCancellationModal
            info={modal}
            onClick={() => handleAction(modal.requires.forwards, 'cancel')}
          />
        );
        break;
      default:
        modalContent = (
          <div className="loading">{CustomIcons.loader} Loading...</div>
        );
        break;
    }
    return (
      <Wrapper10 modal={propsModal} isPreview={isPreview} nevermind={nevermind}>
        {modalContent}
      </Wrapper10>
    );
  };

  const renderComponent = (): React.ReactNode => getModalContent();

  const renderPreview = (): React.ReactNode => {
    const content = getModalContent();

    const dialogStyle = [
      css`
        width: 100%;
      `,
      !offerManagementPage &&
        css`
          display: flex;
          align-items: center;
        `,
    ];
    const isOfferPack = isOfferCardPack(offerContext?.offer);
    const contentStyle = css`
      max-width: ${isBanner || isOfferPack ? '1480' : '1024'}px;
      width: ${isBanner ? '1480px' : isOfferPack ? '100%' : 'auto'};
      margin: auto;
      font-family: ${theme.fontFamily};
      font-size: ${theme.fontSize};
      .modal-content {
        margin: auto;
      }
      > div {
        background: ${theme.modalBackground};
        margin: auto;
      }
    `;

    if (content) {
      return (
        <div className="BrightBox" css={dialogStyle}>
          <div css={contentStyle}>{content}</div>
        </div>
      );
    }
    // FIXME: remove (factory) when no modal is found
    return (
      <div className="bbk-dmmpc-top-preview-no-modal">
        There is no modal (factory) for this reason.
      </div>
    );
  };

  return isPreview ? renderPreview() : renderComponent();
};

export default ModalsFactory;
